<template>
  <div class="monthly-reports">
    <div v-if="isLoadingOptions" class="monthly-state-card">
      <span class="monthly-spinner" aria-hidden="true"></span>
      <div>
        <strong>Loading report options</strong>
        <div class="text-sm text-grey">Checking available payroll months and years...</div>
      </div>
    </div>

    <template v-else>
      <div class="vx-row">
        <div class="vx-col lg:w-2/3 w-full">
          <div class="monthly-section-header">
            <div>
              <h5>Choose reports</h5>
              <span class="text-sm text-grey">
                {{ selected.length }} of {{ reportItems.length }} selected
              </span>
            </div>
            <div class="monthly-selection-actions">
              <vs-button size="small" type="border" :disabled="isProcessing || allReportsSelected" @click="selectAllReports">
                Select all
              </vs-button>
              <vs-button size="small" type="border" color="danger" :disabled="isProcessing || selected.length === 0" @click="selected = []">
                Clear
              </vs-button>
            </div>
          </div>

          <div v-for="group in reportGroups" :key="group.format" class="monthly-report-group">
            <div class="monthly-report-group-title">
              <h6>{{ group.format }} reports</h6>
              <span class="text-sm text-grey">{{ group.items.length }} available</span>
            </div>
            <div class="monthly-report-grid">
              <label
                v-for="item in group.items"
                :key="item.value"
                class="monthly-report-option"
                :class="{ 'is-selected': selected.includes(item.value) }"
              >
                <vs-checkbox v-model="selected" :vs-value="item.value" :disabled="isProcessing">
                  {{ item.reportName }}
                </vs-checkbox>
                <span class="monthly-format-badge" :class="`is-${item.format.toLowerCase()}`">
                  {{ item.format }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="vx-col lg:w-1/3 w-full">
          <div class="monthly-filter-card">
            <h5>Report period</h5>

            <div class="monthly-field">
              <label for="monthly-year">Year <span class="text-danger">*</span></label>
              <v-select id="monthly-year" v-model="yearScope" :options="availableYears" :disabled="isProcessing" placeholder="Select year" />
            </div>

            <div class="monthly-field">
              <label for="monthly-month">Month <span class="text-danger">*</span></label>
              <v-select id="monthly-month" v-model="monthName" :options="availableMonths" :disabled="isProcessing || !yearScope" placeholder="Select month" />
            </div>

            <div v-if="requiresEmployeeCode" class="monthly-field">
              <label for="monthly-employee">Employee <span class="text-danger">*</span></label>
              <v-select
                id="monthly-employee"
                v-model="selectedEmployee"
                :options="employees"
                :get-option-label="getEmployeeLabel"
                :disabled="isProcessing || isLoadingEmployees"
                :placeholder="isLoadingEmployees ? 'Loading employees...' : 'Select an employee'"
              />
              <span class="text-sm text-grey">Required for Gross Pay reports.</span>
              <span v-if="employeeLoadError" class="text-danger text-sm">{{ employeeLoadError }}</span>
            </div>

            <div v-if="selected.length > 0" class="monthly-field">
              <label>Download protection</label>
              <div class="monthly-protection-options">
                <label class="monthly-protection-option" :class="{ 'is-selected': passwordProtection === 'password' }">
                  <input v-model="passwordProtection" type="radio" value="password" :disabled="isProcessing" />
                  <span>
                    <strong>Password</strong>
                    <small>Protect the downloaded ZIP file</small>
                  </span>
                </label>
                <label class="monthly-protection-option" :class="{ 'is-selected': passwordProtection === 'none' }">
                  <input v-model="passwordProtection" type="radio" value="none" :disabled="isProcessing" />
                  <span>
                    <strong>No password</strong>
                    <small>Download the ZIP without a password</small>
                  </span>
                </label>
              </div>
            </div>

            <div class="monthly-output-summary">
              <span class="text-sm text-grey">Download format</span>
              <strong>{{ outputSummary }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isProcessing" class="monthly-state-card processing">
        <span class="monthly-spinner" aria-hidden="true"></span>
        <div class="monthly-progress-content">
          <strong>{{ processTitle }}</strong>
          <div class="text-sm text-grey">{{ processMessage }}</div>
          <div v-if="downloadProgress !== null" class="monthly-progress-track">
            <div class="monthly-progress-fill" :style="{ width: downloadProgress + '%' }"></div>
          </div>
        </div>
        <strong v-if="downloadProgress !== null">{{ downloadProgress }}%</strong>
      </div>

      <div class="monthly-actions">
        <span v-if="validationMessage" class="text-danger text-sm">{{ validationMessage }}</span>
        <vs-button
          color="success"
          type="filled"
          icon-pack="feather"
          icon="icon-download"
          :disabled="!canGenerate || isProcessing"
          @click="requestPassword"
        >
          {{ isProcessing ? "Preparing download..." : "Generate and download" }}
        </vs-button>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import vSelect from "vue-select";
import axios from "@/axios.js";
import Swal from "sweetalert2";

export default {
  components: {
    "v-select": vSelect,
  },
  data() {
    return {
      monthName: null,
      yearScope: null,
      selectedEmployee: null,
      selected: [],
      passwordToOpen: "",
      passwordProtection: "password",
      activeTenant: null,
      isLoadingOptions: true,
      isLoadingEmployees: false,
      employeeLoadError: "",
      isProcessing: false,
      processStage: "",
      processMessage: "",
      downloadProgress: null,
      reportItems: [
        { value: "PayrollSSS-Monthly", reportName: "SSS Monthly Report", format: "PDF" },
        { value: "PayrollPhilhealth-Monthly", reportName: "PhilHealth Monthly Report", format: "PDF" },
        { value: "PayrollPagibig-Monthly", reportName: "Pag-IBIG Monthly Report", format: "PDF" },
        { value: "PayrollLoanPayments-Monthly", reportName: "Loan Payments", format: "PDF" },
        { value: "PayrollDeductions-Monthly", reportName: "Pag-IBIG MP2", format: "PDF" },
        { value: "PayrollGrossPay-History", reportName: "Gross Pay History", format: "PDF" },
        { value: "PayrollWithholdingTax-Monthly", reportName: "Total Compensation with Withholding Tax", format: "PDF" },
        { value: "PayrollWithholdingTaxZero-Monthly", reportName: "Total Compensation with Zero Withholding Tax", format: "PDF" },
        { value: "GrossPay", reportName: "Gross Pay", format: "Excel" },
        { value: "MonthlySSS", reportName: "SSS Monthly", format: "Excel" },
        { value: "MonthlyPhilHealth", reportName: "PhilHealth Monthly", format: "Excel" },
        { value: "MonthlyPagIbig", reportName: "Pag-IBIG Monthly", format: "Excel" },
        { value: "MonthlyLoan", reportName: "Loan Monthly", format: "Excel" },
        { value: "MonthlyTax", reportName: "Tax Monthly", format: "Excel" },
      ],
    };
  },
  computed: {
    getAllCreatePayroll() {
      return this.$store.state.payroll.getAllCreatePayroll || [];
    },
    availableYears() {
      return [...new Set(this.getAllCreatePayroll.map((item) => item.calendarYear))]
        .filter((year) => year !== null && year !== undefined && year !== "")
        .sort((a, b) => Number(b) - Number(a));
    },
    availableMonths() {
      if (!this.yearScope) return [];

      const months = this.getAllCreatePayroll
        .filter((item) => String(item.calendarYear) === String(this.yearScope))
        .map((item) => item.monthName)
        .filter((month) => month !== null && month !== undefined && month !== "");

      return [...new Set(months)].sort((a, b) => this.getMonthNumber(a) - this.getMonthNumber(b));
    },
    tenantName() {
      return (id) => this.$store.getters["tenants/tenantName"](id);
    },
    employees() {
      return (this.$store.state.hrms.allEmployee || [])
        .filter((employee) => employee && employee.id && employee.employeeCode)
        .slice()
        .sort((a, b) => this.getEmployeeLabel(a).localeCompare(this.getEmployeeLabel(b)));
    },
    requiresEmployeeCode() {
      return this.selected.some((reportName) =>
        ["PayrollGrossPay-History", "GrossPay"].includes(reportName)
      );
    },
    reportGroups() {
      return ["PDF", "Excel"].map((format) => ({
        format,
        items: this.reportItems.filter((item) => item.format === format),
      }));
    },
    hasPdfReports() {
      return this.reportItems.some((item) => item.format === "PDF" && this.selected.includes(item.value));
    },
    hasExcelReports() {
      return this.reportItems.some((item) => item.format === "Excel" && this.selected.includes(item.value));
    },
    selectedExportTypes() {
      return [...new Set(
        this.reportItems
          .filter((item) => this.selected.includes(item.value))
          .map((item) => item.format.toUpperCase())
      )];
    },
    outputSummary() {
      const fileDescription = this.hasPdfReports && this.hasExcelReports
        ? "PDF and Excel files"
        : this.hasExcelReports
          ? "Excel files"
          : "PDF files";
      const protectionDescription = this.passwordProtection === "password"
        ? "a password-protected ZIP file"
        : "a ZIP file without a password";
      return `${fileDescription} in ${protectionDescription}`;
    },
    allReportsSelected() {
      return this.selected.length === this.reportItems.length;
    },
    canGenerate() {
      return (
        this.selected.length > 0 &&
        Boolean(this.yearScope) &&
        Boolean(this.monthName) &&
        (!this.requiresEmployeeCode || Boolean(this.selectedEmployee && this.selectedEmployee.id))
      );
    },
    validationMessage() {
      if (this.selected.length === 0) return "Select at least one report.";
      if (!this.yearScope) return "Select a report year.";
      if (!this.monthName) return "Select a report month.";
      if (this.requiresEmployeeCode && !this.selectedEmployee) return "Select an employee for the selected Gross Pay report.";
      return "";
    },
    processTitle() {
      return this.processStage === "download" ? "Downloading reports" : "Generating reports";
    },
  },
  watch: {
    yearScope() {
      if (this.monthName && !this.availableMonths.includes(this.monthName)) this.monthName = null;
    },
    requiresEmployeeCode(required) {
      if (!required) {
        this.selectedEmployee = null;
        this.employeeLoadError = "";
      } else {
        this.ensureEmployees();
      }
    },
  },
  created() {
    this.loadReportOptions();
  },
  methods: {
    async loadReportOptions() {
      this.isLoadingOptions = true;
      try {
        await Promise.all([
          this.$store.dispatch("payroll/getAllCreatePayroll"),
          this.loadActiveTenant(),
        ]);

        if (this.availableYears.length > 0) this.yearScope = this.availableYears[0];
      } catch (error) {
        const message = await this.getErrorMessage(error);
        this.$alert(message, "Unable to load monthly report options", "error");
      } finally {
        this.isLoadingOptions = false;
      }
    },
    getMonthNumber(month) {
      const numericMonth = Number(month);
      if (Number.isInteger(numericMonth) && numericMonth >= 1 && numericMonth <= 12) return numericMonth;

      const parsedMonth = moment(month, "MMMM", true);
      return parsedMonth.isValid() ? parsedMonth.month() + 1 : 99;
    },
    selectAllReports() {
      this.selected = this.reportItems.map((item) => item.value);
    },
    getEmployeeLabel(employee) {
      if (!employee) return "";

      const fullName = employee.fullName || [employee.firstName, employee.middleName, employee.lastName]
        .filter(Boolean)
        .join(" ");
      return [employee.employeeCode, fullName].filter(Boolean).join(" - ");
    },
    async ensureEmployees() {
      if (this.employees.length > 0 || this.isLoadingEmployees) return;

      this.isLoadingEmployees = true;
      this.employeeLoadError = "";
      try {
        await this.$store.dispatch("hrms/getAllEmployee", {
          pageNumber: -1,
          pageSize: -1,
        });
      } catch (error) {
        this.employeeLoadError = "Unable to load employees. Try selecting the report again.";
      } finally {
        this.isLoadingEmployees = false;
      }
    },
    async requestPassword() {
      if (!this.canGenerate || this.isProcessing) return;

      if (this.passwordProtection === "none") {
        this.passwordToOpen = "";
        await this.generateAndDownloadReports();
        return;
      }

      const result = await Swal.fire({
        title: "Protect generated reports",
        input: "password",
        inputLabel: this.hasPdfReports
          ? "Set a password for the ZIP and PDF files"
          : "Set a password for the ZIP file",
        inputPlaceholder: "1 to 10 characters",
        showCancelButton: true,
        confirmButtonText: "Generate",
        inputAttributes: {
          maxlength: 10,
          autocapitalize: "off",
          autocorrect: "off",
        },
        inputValidator: (value) => value && value.trim() ? undefined : "A password is required for these reports.",
      });

      if (!result.isConfirmed) return;
      this.passwordToOpen = result.value;
      await this.generateAndDownloadReports();
    },
    async getLogoPath(tenant) {
      if (!tenant.photoId) return "";

      try {
        const response = await this.$store.dispatch("getFile", tenant.photoId);
        return response.data.path || "";
      } catch (error) {
        return "";
      }
    },
    getTenantId(source) {
      if (!source) return "";
      return source.tenantsId || source.tenantId || source.TenantsId || source.TenantId || "";
    },
    async loadActiveTenant() {
      const activeUser = this.$store.state.AppActiveUser || {};
      let tenantId = this.getTenantId(activeUser);

      if (!tenantId) {
        const profileResponse = await axios.get("/api/account/profile");
        const profile = profileResponse.data.data || profileResponse.data.Data || {};
        tenantId = this.getTenantId(profile);

        if (tenantId) {
          this.$store.dispatch("updateUserInfo", { tenantsId: tenantId });
        }
      }

      if (!tenantId) throw new Error("Your account does not have a company assigned.");

      const tenantResponse = await axios.get(`/api/tenants/${encodeURIComponent(tenantId)}`);
      this.activeTenant = tenantResponse.data.data || tenantResponse.data.Data || null;

      if (!this.activeTenant) throw new Error("The company assigned to your account could not be loaded.");
      return this.activeTenant;
    },
    getActiveTenant() {
      if (this.activeTenant) return this.activeTenant;

      const activeUser = this.$store.state.AppActiveUser || {};
      const activeTenantId = this.getTenantId(activeUser);
      if (!activeTenantId) return null;

      const tenantFromGetter = this.tenantName(activeTenantId);
      if (tenantFromGetter) return tenantFromGetter;

      const normalizedId = String(activeTenantId).toLowerCase();
      const tenantState = this.$store.state.tenants || {};
      const tenants = tenantState.tenants || [];
      return tenants.find((tenant) => String(this.getTenantId(tenant)).toLowerCase() === normalizedId) || null;
    },
    getReportUserId() {
      if (!this.requiresEmployeeCode) return this.$store.state.AppActiveUser.uid;

      if (!this.selectedEmployee || !this.selectedEmployee.id) {
        throw new Error("Select an employee for the selected Gross Pay report.");
      }
      return this.selectedEmployee.id;
    },
    async generateAndDownloadReports() {
      this.isProcessing = true;
      this.processStage = "generate";
      this.processMessage = "The report service is preparing your selected files.";
      this.downloadProgress = null;

      Swal.fire({
        title: "Generating monthly reports",
        html: "Please keep this page open. This can take several minutes.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const tenant = this.getActiveTenant();
        if (!tenant) {
          await this.loadActiveTenant();
        }

        const resolvedTenant = this.getActiveTenant();
        if (!resolvedTenant) throw new Error("The company assigned to your account could not be loaded.");

        const logoPath = await this.getLogoPath(resolvedTenant);
        const userId = this.getReportUserId();
        const monthNumber = this.getMonthNumber(this.monthName);
        if (monthNumber === 99) throw new Error("The selected report month is invalid.");

        const normalizedMonthName = moment()
          .month(monthNumber - 1)
          .format("MMMM");
        const payload = {
          serverName: resolvedTenant.secretKey,
          dbName: resolvedTenant.apiKey,
          userName: "",
          password: "",
          companyCode: resolvedTenant.code,
          companyName: resolvedTenant.name,
          address: resolvedTenant.address,
          photoURL: logoPath,
          createPayrollId: 0,
          printedBy: this.$store.state.AppActiveUser.uid,
          reportName: this.selected.slice(),
          ExportTypes: this.selectedExportTypes,
          payOutDate: moment(`${this.yearScope}-${monthNumber}-01`, "YYYY-M-DD").format("YYYYMMDD"),
          creditDate: moment().format("DD MMM YYYY"),
          usePasswordProtection: this.passwordProtection === "password",
          passwordToOpen: this.passwordToOpen,
          monthName: normalizedMonthName,
          yearScope: String(this.yearScope),
          dbMonthName: String(monthNumber),
          dbCalendarYear: String(this.yearScope),
          dbEmployeeCode: this.requiresEmployeeCode
            ? this.selectedEmployee.employeeCode
            : null,
          reportType: "MONTHLY",
          userId: [userId],
        };

        await this.$store.dispatch("payroll/GenerateReports", payload);

        this.processStage = "download";
        this.processMessage = "Reports are ready. Downloading the ZIP file now.";
        Swal.update({
          title: "Downloading monthly reports",
          html: "Reports were generated successfully. Preparing the ZIP download...",
        });

        const zipFileName = `${resolvedTenant.code}_MONTHLY.zip`;
        const response = await axios.get("/api/reports/OtherReports", {
          params: {
            dbName: resolvedTenant.code,
            fileName: zipFileName,
            reportType: "MONTHLY",
            monthName: normalizedMonthName,
            yearScope: String(this.yearScope),
          },
          responseType: "blob",
          timeout: 120000,
          onDownloadProgress: (event) => {
            if (!event.total) return;
            this.downloadProgress = Math.min(100, Math.round((event.loaded / event.total) * 100));
          },
        });

        this.validateDownloadResponse(response);
        this.downloadBlob(response.data, zipFileName);
        Swal.close();
        await Swal.fire({
          title: "Reports downloaded",
          html: `${this.selected.length} monthly report${this.selected.length === 1 ? "" : "s"} generated successfully.`,
          icon: "success",
          timer: 2500,
          timerProgressBar: true,
        });
      } catch (error) {
        const message = await this.getErrorMessage(error);
        Swal.close();
        await Swal.fire({ title: "Monthly report failed", text: message, icon: "error" });
      } finally {
        this.isProcessing = false;
        this.processStage = "";
        this.processMessage = "";
        this.downloadProgress = null;
      }
    },
    validateDownloadResponse(response) {
      if (!response.data || response.data.size === 0) throw new Error("The server returned an empty ZIP file.");

      const contentType = String(response.headers["content-type"] || "").toLowerCase();
      if (contentType.includes("json") || contentType.includes("text/")) {
        throw new Error("The server returned an error instead of the ZIP file.");
      }
    },
    downloadBlob(blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    readBlobAsText(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });
    },
    async getErrorMessage(error) {
      if (error && error.response && error.response.data instanceof Blob) {
        try {
          const text = await this.readBlobAsText(error.response.data);
          const parsed = JSON.parse(text);
          return this.extractApiMessage(parsed) || text;
        } catch (parseError) {
          return error.message || "The report service returned an unreadable response.";
        }
      }

      const responseData = error && error.response && error.response.data;
      return (
        this.extractApiMessage(responseData) ||
        (error && error.message) ||
        "Unable to generate monthly reports."
      );
    },
    extractApiMessage(payload) {
      if (!payload) return "";
      if (typeof payload === "string") return payload;

      const message = payload.message || payload.Message;
      if (typeof message === "string") return message;
      if (!message) return "";

      return (
        message.text ||
        message.Text ||
        message.exception ||
        message.Exception ||
        message.title ||
        message.Title ||
        ""
      );
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";

.monthly-reports {
  padding: 0.25rem;
}

.monthly-section-header,
.monthly-actions,
.monthly-state-card,
.monthly-report-option,
.monthly-selection-actions {
  display: flex;
  align-items: center;
}

.monthly-section-header,
.monthly-actions {
  justify-content: space-between;
  gap: 1rem;
}

.monthly-selection-actions {
  gap: 0.5rem;
}

.monthly-report-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.65rem;
}

.monthly-report-group {
  margin-top: 1.25rem;
}

.monthly-report-group-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.monthly-report-option {
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 58px;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.monthly-report-option.is-selected {
  border-color: rgba(var(--vs-primary), 0.65);
  background: rgba(var(--vs-primary), 0.06);
}

.monthly-format-badge {
  flex: 0 0 auto;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  color: #555;
  background: #eef0f4;
  font-size: 0.7rem;
  font-weight: 600;
}

.monthly-format-badge.is-excel {
  color: #19764a;
  background: #e2f4e9;
}

.monthly-filter-card {
  height: 100%;
  padding: 1.25rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.025);
}

.monthly-field {
  margin-top: 1rem;
}

.monthly-field > label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
}

.monthly-protection-options {
  display: grid;
  gap: 0.5rem;
}

.monthly-protection-option {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 7px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.monthly-protection-option.is-selected {
  border-color: rgba(var(--vs-primary), 0.65);
  background: rgba(var(--vs-primary), 0.06);
}

.monthly-protection-option input {
  margin-top: 0.2rem;
}

.monthly-protection-option span {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.monthly-protection-option small {
  color: #777;
}

.monthly-output-summary {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.monthly-state-card {
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border: 1px solid rgba(var(--vs-primary), 0.2);
  border-radius: 8px;
  background: rgba(var(--vs-primary), 0.04);
}

.monthly-state-card.processing {
  justify-content: space-between;
}

.monthly-progress-content {
  flex: 1;
}

.monthly-progress-track {
  height: 5px;
  margin-top: 0.6rem;
  overflow: hidden;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.1);
}

.monthly-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: rgb(var(--vs-success));
  transition: width 0.2s ease;
}

.monthly-spinner {
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  border: 3px solid rgba(var(--vs-primary), 0.2);
  border-top-color: rgb(var(--vs-primary));
  border-radius: 50%;
  animation: monthly-spin 0.8s linear infinite;
}

.monthly-actions {
  margin-top: 1.5rem;
}

@keyframes monthly-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .monthly-report-grid {
    grid-template-columns: 1fr;
  }

  .monthly-filter-card {
    margin-top: 1rem;
  }
}

@media (max-width: 600px) {
  .monthly-section-header,
  .monthly-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .monthly-selection-actions {
    justify-content: flex-start;
  }
}
</style>
